import { useEffect, useState, useCallback } from "react";
import { Modal, Button, Dropdown, Spinner, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRegistered, faHeart as solidHeart, faInfoCircle, faWarning } from "@fortawesome/free-solid-svg-icons";
import { faHeart as regularHeart } from "@fortawesome/free-regular-svg-icons";
import { ReactComponent as ZeusLogo } from "../../images/zeus_bolt_black_outline.svg";
import { useNavigate } from "react-router-dom";

import PropertyNotes from "../PropertyNotes";
import { processAuctionDate } from "../utils";
import {
  returnPhysicalAddress,
  injectHardCodedRegistrationDates,
  registrationEndDateToDisplay,
  isHybridSale,
  isOnlineOrZeus,
} from "../../utilities/hybridSales";
import US_STATES from "../../constants/states";

import PropertyField from "./shared/PropertyField";
import { TaxSaleDetails, ForeclosureDetails } from "./modal/SaleDetails";
import { useLazyGetPropertyDocumentsQuery, useLazyGetDownloadDocumentQuery } from "../../redux/documentsSlice";
import { handleDocumentDownload } from "../../utilities/documentsUtils";

import "./PropertyDetailsModal.css";

// --- Document category dropdown ---
const DocumentCategoryDropdown = ({ category, documents, onDownload }) => {
  const filtered = documents.filter(
    (doc) =>
      doc.uploadedFileName && doc.category.includes(category) && (!doc.expDate || new Date(doc.expDate) > new Date())
  );

  if (filtered.length === 0) return null;

  return (
    <div className="document-category">
      <Dropdown bsPrefix="dropdown" container={document.body}>
        <Dropdown.Toggle
          id={`dropdown-${category}`}
          className="btn btn-sm custom-dropdown-download-button"
        >
          {`${category} Documents`}
        </Dropdown.Toggle>
        <Dropdown.Menu style={{ maxHeight: "200px", overflowY: "auto" }}>
          {filtered.map((doc, idx) => (
            <Dropdown.Item
              key={idx}
              onClick={() => onDownload(doc.uploadedFileName, doc.storedFileName, doc.containerName)}
            >
              {doc.uploadedFileName}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

// --- County info section ---
const CountyInfo = ({ detailsResponse, onNavigate, countyDisplayName }) => (
  <div className="county-section">
    <div className="county-info-heading d-flex align-items-center flex-wrap gap-2 pb-3">
      <button
        type="button"
        onClick={onNavigate}
        className="countyInfoBtn d-flex gap-2 mx-0 p-0"
        aria-label={`View ${countyDisplayName} county information`}
      >
        <span>{countyDisplayName}</span>
        <FontAwesomeIcon icon={faInfoCircle} size="sm" aria-hidden="true" />
      </button>
      {/* Outside the button so WAVE does not inherit button fg/bg; no Bootstrap .badge (sets color:#fff). */}
      <span className="county-new-pill" aria-hidden="true">
        New
      </span>
    </div>
    <PropertyField label="County Address" value={detailsResponse?.countyDetail?.address} />
    <PropertyField label="Newspaper of Record" value={detailsResponse?.countyDetail?.newspaper} />
    <PropertyField label="SRI Contact" value="1-800-800-9588" />
    <PropertyField label="Tax Sale Support" value="support@sriservices.com" />
    <PropertyField label="Sheriff Sale Support" value="sheriffsalesupport@sriservices.com" />
  </div>
);

/**
 * Modal displaying full property details, map, documents, and action buttons.
 */
const PropertyDetailsModal = ({
  showModal,
  setShowModal,
  selectedProperty,
  propertyDetails,
  detailsResponse,
  showDirections,
  directions,
  handleGetDirections,
  isPropertyRegistered,
  getRegisterButtonLabel,
  handleRegisterOrUnregisterClick,
  isPropertyFavorite,
  getFavoriteButtonLabel,
  handleFavoriteClick,
  isLoadingInfo,
  countyDisplayName,
}) => {
  const [minimumBid, setMinimumBid] = useState("N/A");
  const [saleLocalTime, setSaleLocalTime] = useState("Invalid Date/Time");
  const [userLocalTime, setUserLocalTime] = useState("Invalid Date/Time");
  const [showSaleTimeToggle, setShowSaleTimeToggle] = useState(true);

  const [triggerPropertyDocuments, { isLoading: isLoadingDocumentsResult, data: propertyDocumentsData }] = useLazyGetPropertyDocumentsQuery();
  const [triggerDownloadDocument] = useLazyGetDownloadDocumentQuery();

  const { isLoaded, loadError } = isLoadingInfo;
  const navigate = useNavigate();

  // --- Time processing ---
  const processTime = useCallback(() => {
    if (!selectedProperty?.date || !selectedProperty?.time || !selectedProperty?.state || !selectedProperty?.county) {
      setSaleLocalTime("Invalid Date/Time");
      setUserLocalTime("Invalid Date/Time");
      return;
    }

    const [monthStr, dayStr, yearStr] = selectedProperty.date.split("/");
    if (!monthStr || !dayStr || !yearStr) return;

    const [timePart, meridian] = selectedProperty.time.split(" ");
    if (!timePart || !meridian) return;

    let [hours, minutes] = timePart.split(":");
    hours = parseInt(hours, 10);
    minutes = parseInt(minutes, 10) || 0;

    if (meridian.toUpperCase() === "PM" && hours !== 12) hours += 12;
    else if (meridian.toUpperCase() === "AM" && hours === 12) hours = 0;

    const month = monthStr.padStart(2, "0");
    const day = dayStr.padStart(2, "0");
    const isoDateTime = `${yearStr}-${month}-${day}T${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}:00`;

    const { saleLocalTime: sLocal, userLocalTime: uLocal } = processAuctionDate(
      isoDateTime,
      selectedProperty.state,
      selectedProperty.county
    );

    setSaleLocalTime(sLocal.includes(",") ? sLocal.split(",")[1].trim() : sLocal);
    setUserLocalTime(uLocal.includes(",") ? uLocal.split(",")[1].trim() : uLocal);
  }, [selectedProperty]);

  // --- Documents ---
  const fetchDocuments = useCallback(async () => {
    if (!selectedProperty) return;
    try {
      const stateCode = US_STATES.find((state) => state.name === selectedProperty.state)?.code || selectedProperty.state;
      await triggerPropertyDocuments({
        id: selectedProperty.id,
        propertyId: selectedProperty.propertyId,
        saleId: selectedProperty.saleId,
        saleType: selectedProperty.saleType,
        state: stateCode,
        county: selectedProperty.county,
        auctionDate: selectedProperty.auctionDate,
      });
    } catch (error) {
      console.error("Error fetching property documents:", error);
    }
  }, [selectedProperty, triggerPropertyDocuments]);

  useEffect(() => {
    if (showModal && selectedProperty) {
      setMinimumBid(propertyDetails?.minimumBid || "N/A");
      processTime();
      fetchDocuments();
    }
  }, [showModal, selectedProperty, processTime, fetchDocuments, propertyDetails]);

  // --- Derived values ---
  const isTaxSale = ["Tax Sale", "Adjudicated Sale", "Tax Lien Sale"].includes(selectedProperty?.saleTypeDescription);
  const isForeclosure = selectedProperty?.saleTypeDescription === "Foreclosure";
  const isCancelled = !!selectedProperty?.cancelled;
  const registrationSaleContext = {
    ...selectedProperty,
    type: selectedProperty?.type || detailsResponse?.auctionDetail?.type,
    location: selectedProperty?.location || detailsResponse?.auctionDetail?.location,
    auctionLocation:
      selectedProperty?.auctionLocation || detailsResponse?.auctionDetail?.location,
  };
  const isHybrid = isOnlineOrZeus(registrationSaleContext) === "Online Registration/In-Person Sale";
  const isOnlineAuction = isOnlineOrZeus(registrationSaleContext) === "Online/Zeus";

  const auctionDetail = injectHardCodedRegistrationDates(detailsResponse?.auctionDetail);
  const regEndDate = registrationEndDateToDisplay(auctionDetail);

  const saleLocation =
    auctionDetail &&
    selectedProperty?.date &&
    auctionDetail.date &&
    selectedProperty.date.trim() === auctionDetail.date.trim() &&
    auctionDetail.location?.trim()
      ? auctionDetail.location
      : selectedProperty?.location ?? "N/A";

  const displayLocation = isHybridSale(saleLocation) ? returnPhysicalAddress(saleLocation) : saleLocation;

  const showPurchasePrice = () => {
    const auctionDate = new Date(selectedProperty?.auctionDate);
    return auctionDate < new Date() && isForeclosure;
  };

  return (
    <Modal
      show={showModal}
      onHide={() => setShowModal(false)}
      size="lg"
      centered
      scrollable
      dialogClassName={`property-details-modal${isCancelled ? " is-cancelled" : ""}`}
      aria-labelledby="property-details-modal-title"
    >
      <Modal.Header closeButton>
        <Modal.Title as="h2" id="property-details-modal-title">Property Details</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {selectedProperty && (
          <>
            <h3 className="property-address">{selectedProperty.address1}</h3>

            {/* Hybrid sale warning */}
            {isHybridSale(selectedProperty?.location) && (
              <div className="hybrid-sale-warning" role="note">
                <FontAwesomeIcon icon={faWarning} aria-hidden="true" /> No on-site registration. Registration must be completed online
                prior to <strong>{regEndDate}</strong>.
              </div>
            )}

            {selectedProperty.saleStatusDescription && (
              <PropertyField label="Status" value={selectedProperty.saleStatusDescription} />
            )}

            {/* Main details section */}
            <div className="property-details-container">
              {/* Date */}
              <PropertyField label="Date" value={selectedProperty.date} />

              {/* Sale time with toggle */}
              <PropertyField label="Sale Time">{showSaleTimeToggle ? saleLocalTime : userLocalTime}</PropertyField>
              <Form.Check
                type="switch"
                id={`property-modal-time-${selectedProperty.id}-${selectedProperty.propertyId}`}
                label={`Show ${showSaleTimeToggle ? "User Local Time" : "Sale Local Time (County)"}`}
                checked={showSaleTimeToggle}
                onChange={() => setShowSaleTimeToggle((prev) => !prev)}
                className="time-toggle-switch"
              />

              {/* Sale-type-specific fields */}
              {isTaxSale ? (
                <TaxSaleDetails
                  selectedProperty={selectedProperty}
                  detailsResponse={detailsResponse}
                  minimumBid={minimumBid}
                  displayLocation={displayLocation}
                  isCancelled={isCancelled}
                />
              ) : (
                <ForeclosureDetails
                  selectedProperty={selectedProperty}
                  propertyDetails={propertyDetails}
                  minimumBid={minimumBid}
                  displayLocation={displayLocation}
                  showPurchasePrice={showPurchasePrice()}
                />
              )}
            </div>

            {/* Notes */}
            <div className="property-notes-section">
              {selectedProperty.propertyId && <PropertyNotes propertyId={selectedProperty.propertyId} />}
            </div>

            {/* County info */}
            <CountyInfo
              detailsResponse={detailsResponse} countyDisplayName={countyDisplayName}
              onNavigate={() => {
                const { countyDetail } = detailsResponse;
                navigate(`/county/${countyDetail.stateCode}/${countyDetail.countyName}`);
              }}
            />
            
            {/* Documents */}
            <div className="documents-section">
              {isLoadingDocumentsResult ? (
                <div className="documents-loading">
                  <Spinner animation="border" role="status" className="documents-spinner">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <strong>Documents are being fetched...</strong>
                </div>
              ) : (
                ["General", "Sale", "State", "County", "Property", "SRI", "Tax"].map((cat) => (
                  <DocumentCategoryDropdown
                    key={cat}
                    category={cat}
                    documents={Array.isArray(propertyDocumentsData) ? propertyDocumentsData : []}
                    onDownload={(uploadedFileName, storedFileName, containerName) =>
                      handleDocumentDownload(uploadedFileName, storedFileName, containerName, triggerDownloadDocument)
                    }
                  />
                ))
              )}
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowModal(false)}>
          Close
        </Button>

        {/* Hybrid — online registration, in-person sale */}
        {isHybrid && (
          <Button 
            variant="outline-primary" 
            href={isCancelled ? undefined : "http://www.zeusauction.com"}
            target={isCancelled ? undefined : "_blank"}
            rel={isCancelled ? undefined : "noopener noreferrer"}
            disabled={isCancelled}
          >
            <FontAwesomeIcon icon={faRegistered} aria-hidden="true" /> Online Reg / In-Person Sale
          </Button>
        )}

        {/* Online — full Zeus auction */}
        {!isHybrid && isOnlineAuction && (
          <Button 
            variant="outline-primary" 
            href={isCancelled ? undefined : "http://www.zeusauction.com"}
            target={isCancelled ? undefined : "_blank"}
            rel={isCancelled ? undefined : "noopener noreferrer"}
            disabled={isCancelled}
          >
            <ZeusLogo width={20} height={20} /><span>Register</span>
          </Button>
        )}

        {/* Standard — non-foreclosure, non-online */}
        {!isHybrid && !isOnlineAuction && !isForeclosure && (
          <Button
            variant={isPropertyRegistered(selectedProperty) ? "secondary" : "outline-secondary"}
            onClick={() =>
              !isCancelled && handleRegisterOrUnregisterClick({
                saleId: selectedProperty.saleId,
                auction: selectedProperty,
              })
            }
            disabled={isCancelled}
          >
            <FontAwesomeIcon icon={faRegistered} aria-hidden="true" /> {getRegisterButtonLabel(selectedProperty)}
          </Button>
        )}

        {/* Foreclosure without Zeus — no registration button */}

        <Button
          className={isPropertyFavorite(selectedProperty) ? "favorite-btn-active" : "favorite-btn-inactive"}
          variant={isPropertyFavorite(selectedProperty) ? "success" : "outline-success"}
          onClick={() => handleFavoriteClick(selectedProperty)}
        >
          <FontAwesomeIcon icon={isPropertyFavorite(selectedProperty) ? solidHeart : regularHeart} aria-hidden="true" />{" "}
          {getFavoriteButtonLabel(selectedProperty)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PropertyDetailsModal;
